import { useEffect, useMemo } from "react";

const SITE_URL = "https://stagehookglobal.com";

// Module-scope helpers — referentially stable across renders.
// Safe (and harmless) to include in effect dep arrays.
const setMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const replaceJsonLd = (serialized) => {
  document
    .querySelectorAll('script[data-jsonld^="page-"]')
    .forEach((s) => s.remove());
  if (!serialized) return;
  const schemas = JSON.parse(serialized);
  const list = Array.isArray(schemas) ? schemas : [schemas];
  list.forEach((schema, idx) => {
    const el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", `page-${idx}`);
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
  });
};

const useSEO = ({ title, description, keywords, image, path, jsonLd }) => {
  const canonical = path ? `${SITE_URL}${path}` : SITE_URL;
  const jsonLdKey = useMemo(
    () => (jsonLd ? JSON.stringify(jsonLd) : ""),
    [jsonLd]
  );

  // Effect 1: title + canonical
  useEffect(() => {
    if (title) document.title = title;
    setLink("canonical", canonical);
  }, [title, canonical]);

  // Effect 2: description + keywords
  useEffect(() => {
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
  }, [description, keywords]);

  // Effect 3: Open Graph + Twitter share tags
  useEffect(() => {
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Stagehook Global");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
  }, [title, description, image, canonical]);

  // Effect 4: JSON-LD structured data
  useEffect(() => {
    replaceJsonLd(jsonLdKey);
  }, [jsonLdKey]);
};

export default useSEO;
